/**
 * Global Footer — premium enterprise footer component.
 * Layout: large rounded card, brand left + menu columns right.
 * Mobile: accordion. All content from CMS via useFooter().
 * Uses design system tokens for all styling.
 */

import { memo } from 'react';
import { Link } from 'react-router-dom';
import { useFooter } from '../../hooks/useFooter';
import { FooterAccordion } from './FooterAccordion';
import { FooterBottom } from './FooterBottom';
import { FooterColumn } from './FooterColumn';
import { FooterSocial } from './FooterSocial';

function FooterSkeleton(): JSX.Element {
  return (
    <footer className="mt-20 mb-10 mx-auto max-w-7xl px-6">
      <div className="rounded-[32px] bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-10 animate-pulse">
        <div className="h-40" />
      </div>
    </footer>
  );
}

export const GlobalFooter = memo(function GlobalFooter(): JSX.Element {
  const { data, isLoading } = useFooter();

  if (isLoading || !data) return <FooterSkeleton />;

  const { settings, menu, watermark } = data;

  // CMS-controlled logo width (px), independently for desktop/mobile —
  // consumed by the `.cms-logo` utility class (globals.css), which
  // switches from the mobile value to the desktop value at the `lg`
  // breakpoint. Mirrors the same mechanism used in the Header.
  const logoSizeVars = {
    '--logo-w-mobile': `${settings.logoWidthMobile}px`,
    '--logo-w-desktop': `${settings.logoWidthDesktop}px`,
  } as React.CSSProperties;

  const alignClass = watermark?.align === 'left' ? 'justify-start' : watermark?.align === 'right' ? 'justify-end' : 'justify-center';

  // Apply text-transform based on CMS setting
  const transformText = (text: string, textCase: string): string => {
    switch (textCase) {
      case 'uppercase': return text.toUpperCase();
      case 'lowercase': return text.toLowerCase();
      case 'capitalize': return text.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
      default: return text;
    }
  };

  return (
    <footer
      className="relative mt-0 mb-0 bg-transparent"
      role="contentinfo"
      aria-label="Site footer"
    >
      {/* Wrapper with relative positioning for the sticky effect */}
      <div className="relative">
        {/* Footer card — overlays the watermark, stops halfway over it */}
        <div
          className="relative z-10 mx-auto px-4 sm:px-6"
          style={{ maxWidth: 'var(--ui-container-width)', marginBottom: 'clamp(-4rem, -7vw, -6rem)' }}
        >
          <div
            className="relative rounded-[32px] border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-8 sm:p-10 lg:p-12 shadow-md dark:shadow-slate-900/50 overflow-hidden"
          >
        {/* Subtle animated grid background */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-[32px]">
          {/* Dot grid */}
          <div className="absolute inset-0 opacity-[0.04] dark:opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle, currentColor 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
          {/* Radial glow at center */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-blue-500/[0.03] dark:bg-blue-400/[0.05] rounded-full blur-3xl animate-[glowFloat_12s_ease-in-out_infinite]" />
        </div>
        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-10 lg:gap-16">
          {/* Left: Brand */}
          <div className="space-y-5">
            {/* Logo — auto switches based on dark/light mode */}
            <Link to="/" className="inline-block">
              {settings.logo && (
                <>
                  <img
                    src={settings.logo}
                    alt={settings.businessName}
                    className="cms-logo dark:hidden"
                    style={logoSizeVars}
                    loading="lazy"
                  />
                  <img
                    src={settings.logoDark || settings.logo}
                    alt={settings.businessName}
                    className="cms-logo hidden dark:block"
                    style={logoSizeVars}
                    loading="lazy"
                  />
                </>
              )}
            </Link>

            {/* Description */}
            <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed max-w-sm whitespace-pre-line">
              {settings.description}
            </p>

            {/* Social Icons */}
            <FooterSocial links={settings.social} />
          </div>

          {/* Right: Menu Columns (Desktop) */}
          <nav aria-label="Footer navigation" className="hidden md:block">
            <div className={`grid gap-8 ${menu.length <= 3 ? 'grid-cols-3' : menu.length === 4 ? 'grid-cols-4' : 'grid-cols-5'}`}>
              {menu.map((col) => (
                <FooterColumn key={col.id} column={col} />
              ))}
            </div>
          </nav>

          {/* Right: Accordion (Mobile) */}
          <div className="md:hidden">
            <FooterAccordion columns={menu} />
          </div>
        </div>

        {/* Bottom Bar */}
        <FooterBottom copyright={settings.copyright} policies={settings.policies} />
        </div>
      </div>

      {/* Background watermark — styled from CMS, responsive */}
      {(watermark?.visibleMobile !== false) && (
      <div
        aria-hidden="true"
        className={`relative w-full overflow-hidden pointer-events-none select-none flex items-start ${alignClass}`}
        style={{ height: 'clamp(5rem, 12vw, 12rem)' }}
      >
        {/* Bottom to top fade */}
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-slate-50 dark:from-slate-900 to-transparent z-10" />

        {/* Desktop watermark */}
        <span
          className="hidden sm:inline"
          style={{
            fontSize: watermark?.fontSize || 'clamp(8rem, 20vw, 22rem)',
            fontFamily: watermark?.fontFamily || "'Plus Jakarta Sans', system-ui, sans-serif",
            fontWeight: watermark?.fontWeight || '900',
            letterSpacing: watermark?.spacing || '-0.02em',
            color: watermark?.color || '#bec8d7',
            opacity: watermark?.opacity ?? 0.5,
            lineHeight: '0.8',
            whiteSpace: 'nowrap',
          }}
        >
          {transformText(watermark?.text || settings.businessName, watermark?.textCase || 'lowercase')}
        </span>

        {/* Mobile watermark — smaller font, full name visible */}
        <span
          className="sm:hidden"
          style={{
            fontSize: watermark?.fontSizeMobile || '4rem',
            fontFamily: watermark?.fontFamily || "'Plus Jakarta Sans', system-ui, sans-serif",
            fontWeight: watermark?.fontWeight || '900',
            letterSpacing: watermark?.spacing || '-0.02em',
            color: watermark?.color || '#bec8d7',
            opacity: watermark?.opacity ?? 0.5,
            lineHeight: '0.85',
            whiteSpace: 'nowrap',
          }}
        >
          {transformText(watermark?.text || settings.businessName, watermark?.textCase || 'lowercase')}
        </span>
      </div>
      )}
      </div>
    </footer>
  );
});

export default GlobalFooter;
